import { format } from "date-fns";
import { nb } from "date-fns/locale";
import React, { useId, useState } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Button,
  Checkbox,
  FieldShell,
  Icon,
  IconButton,
  Textarea,
  TextField,
  styles as ui,
} from "stories/Admin";
import JummahInfoTextarea from "../JummahInfo/JummahInfoTextarea";
import styles from "./AddSpecialTimeModal.module.scss";

interface AddSpecialTimeModalProps {
  show: boolean;
  onClose: () => void;
  onSave: (
    time: string,
    info: string,
    startDate: string,
    endDate: string,
    sameAsIsha?: boolean,
  ) => void;
  activeTab: string;
  /** Antall jummah-tider moskeen får etter lagring (styrer TV-tegngrensen). */
  jummahCount?: number;
}

const MAX_INFO_LENGTH = 255;

const TAB_NAMES: Record<string, string> = {
  jummah: "jummah",
  taraweeh: "taraweeh",
  eid: "eid",
};

const AddSpecialTimeModal: React.FC<AddSpecialTimeModalProps> = ({
  show,
  onClose,
  onSave,
  activeTab,
  jummahCount = 1,
}) => {
  const titleId = useId();
  const startId = useId();
  const endId = useId();
  const [time, setTime] = useState<string>("");
  const [info, setInfo] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [sameAsIsha, setSameAsIsha] = useState<boolean>(false);

  // Handle time input to allow partial inputs and validation on blur, including blank inputs
  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (sameAsIsha) return;

    let value = e.target.value.replace(/[^0-9]/g, "");

    if (value.length > 4) {
      value = value.slice(0, 4);
    }

    if (value.length > 2) {
      value = value.slice(0, 2) + ":" + value.slice(2);
    }

    setTime(value);
  };

  const handleSameAsIshaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setSameAsIsha(isChecked);
    setTime(isChecked ? "00:00" : "");
  };

  // On blur (when the input loses focus), validate or allow blank time
  const handleTimeBlur = () => {
    if (time === "") {
      setTime("");
    } else if (!/^([01]\d|2[0-3]):([0-5]\d)$/.test(time)) {
      setTime("00:00");
    }
  };

  const handleInfoChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value.slice(0, MAX_INFO_LENGTH);
    setInfo(value);
  };

  const handleSave = () => {
    const formattedStartDate = startDate ? format(startDate, "dd-MM") : "";
    const formattedEndDate = endDate ? format(endDate, "dd-MM") : "";
    onSave(time, info, formattedStartDate, formattedEndDate, sameAsIsha);
    onClose();
  };

  if (!show) return null;

  const name = TAB_NAMES[activeTab] ?? activeTab;

  return createPortal(
    <div
      className={ui.overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`${ui.modal} ${styles.modal}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
        }}
      >
        <h2 className={ui.mh} id={titleId}>
          <span className={ui.mhIcon} aria-hidden="true">
            <Icon name="plus" size={18} strokeWidth={2.2} />
          </span>
          Ny {name}-tid
          <IconButton
            label="Lukk"
            icon={<Icon name="x" size={18} />}
            className={styles.close}
            onClick={onClose}
          />
        </h2>
        <div className={ui.mb}>
          <div className={activeTab === "jummah" ? ui.g3 : undefined}>
            <TextField
              label="Tid"
              required
              autoFocus
              inputMode="numeric"
              className={ui.num}
              placeholder="tt:mm"
              value={time}
              onChange={handleTimeChange}
              onBlur={handleTimeBlur}
              disabled={sameAsIsha}
            />

            {activeTab === "jummah" && (
              <>
                <FieldShell
                  id={startId}
                  label="Startdato"
                  required
                  help="dd-mm"
                >
                  <DatePicker
                    id={startId}
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    dateFormat="dd-MM"
                    locale={nb}
                    className={ui.inp}
                    wrapperClassName={styles.picker}
                    portalId="adm-datepicker"
                    autoComplete="off"
                  />
                </FieldShell>
                <FieldShell id={endId} label="Sluttdato" required help="dd-mm">
                  <DatePicker
                    id={endId}
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    dateFormat="dd-MM"
                    locale={nb}
                    className={ui.inp}
                    wrapperClassName={styles.picker}
                    portalId="adm-datepicker"
                    autoComplete="off"
                  />
                </FieldShell>
              </>
            )}
          </div>

          {activeTab === "taraweeh" && (
            <Checkbox
              label="Samme tid som Isha-jamat"
              checked={sameAsIsha}
              onChange={handleSameAsIshaChange}
            />
          )}

          {activeTab === "jummah" ? (
            <JummahInfoTextarea
              label="Tilleggsinformasjon"
              placeholder="F.eks. «Khutbah på norsk»"
              value={info}
              onChange={handleInfoChange}
              maxLength={MAX_INFO_LENGTH}
              jummahCount={jummahCount}
            />
          ) : (
            <Textarea
              label="Tilleggsinformasjon"
              placeholder="F.eks. «Khutbah på norsk»"
              value={info}
              onChange={handleInfoChange}
              maxLength={MAX_INFO_LENGTH}
              maxChars={MAX_INFO_LENGTH}
            />
          )}
        </div>
        <div className={ui.mf}>
          <Button variant="secondary" onClick={onClose}>
            Avbryt
          </Button>
          <Button onClick={handleSave}>Lagre</Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AddSpecialTimeModal;
